import { CollectionPopupComponent } from '../../../../shared';
import { voyageSpendErrorMessage } from '../voyage-credits.model';
import { LocatedVesselRecord } from '../../shared/contracts/vessel.types';
import { AisMapFeature } from '../../shared/contracts/ais-map.feature';
import { AisMapContext } from '../../shared/contracts/ais-map.context';
import { vrMirrorVesselLookup, vrToLocatedRecord } from '../gateways/vr-lookup.gateway';

/**
 * What this feature must be TOLD rather than know — facade glue only, and both
 * for the same reason: they coordinate ACROSS features, which is the one job a
 * feature must not take on itself (rule R3).
 */
export interface LookupFeatureOptions {
  /**
   * Make a located hull the map selection, guard permitting. Facade glue and
   * staying that way: seven call sites across three features and the located
   * layer reach it, and it spans the selection, the track bar's saved list and
   * the credit lines. A feature owning it would be a feature every other
   * feature has to import.
   */
  selectLocated: (mmsi: string, opts?: { openPopup?: boolean; fly?: boolean }) => void;
  /** Re-push every price line after a wallet-moving response (the broadcast). */
  syncCreditLines: () => void;
}

/**
 * THE VESSEL LOOKUP — the paid by-MMSI question, and the free save that sits
 * beside it on the same card (phase 9, REFACTOR-PLAN.md §7).
 *
 * WHY THIS EXISTS AS A PHASE AT ALL: it was the last area still living on the
 * facade, and it was never assigned to one. §6.6's destination table folded it
 * into "located | 825" — reasonable, because it reads and writes located
 * records — but in execution it could not go there: `located.store` is SHARED
 * code, and this normalises Voyage Radar's payload envelope, spends Voyage
 * Radar's credits and speaks Voyage Radar's error copy. Sorting by "what does
 * it touch" put it under located; sorting by "who may own it" puts it here.
 * The 2026-08-13 reconciliation found the gap and this is the answer to it.
 *
 * TWO ACTS, ONE SUBJECT — a hull the user is pointing at:
 *
 *   `refreshVesselLookup` — 1 credit. The latest fix plus the fields nothing
 *   else carries (destination, from port, dimensions). Reached from a history
 *   row's Refresh, the popup's "Get latest" on any hull, and a POI or nearby
 *   row that has no position to draw yet.
 *
 *   `saveSnapshotVessel` — free, and free is the whole point. The AOI refresh
 *   that drew this hull has ALREADY been paid for, so keeping it is a local
 *   copy of data the task owns, not a second purchase.
 *
 * It contributes NO `popupActions()`, deliberately. The card's "Get latest"
 * price is a card BASIC, not a feature contribution: a drawer feature offers a
 * button only while it is mounted (invariant I5 — a null price hides it), and
 * the lookup has no drawer and no mounted state. Leaving `lookupCredits` on
 * the popup host's own options is what keeps it un-clobberable by any feature,
 * which is the guarantee phase 7 had to repair once already.
 *
 * The provider API call inside (`lookupVoyageRadarVessel`) is correct code
 * rather than debt, per D7: features live provider-side and speak to their own
 * endpoints. What is NOT here is the payload dialect — `vrToLocatedRecord` and
 * the sensor-document mirror are in `vr-lookup.gateway.ts`, because three
 * other features need the first of them and none of them may import this file.
 */
export class LookupFeature extends AisMapFeature {
  /**
   * MMSI of the by-MMSI lookup in flight, or null — ONE AT A TIME, across the
   * whole map rather than per hull.
   *
   * Deliberately stricter than the drawer features' per-purchase guards. Those
   * three sit on one card and must not stand each other down; this one is
   * reachable from four places at once (a card, two drawer row lists and the
   * Saved Vessels panel), all of which can be pointing at different hulls. A
   * per-hull guard would let a user with a nearby list open spend four credits
   * in four clicks without ever seeing an answer to the first.
   */
  private loadingMmsi: string | null = null;

  constructor(ctx: AisMapContext, private readonly opts: LookupFeatureOptions) {
    super(ctx);
  }

  /** MMSI of the lookup in flight, or null — the spinner the panel rows read. */
  get lookupLoadingMmsi(): string | null {
    return this.loadingMmsi;
  }

  /** What one lookup costs on this provider, in its own units. */
  private price(): number {
    return this.ctx.spend?.priceFor('lookup') ?? 0;
  }

  /**
   * A task switch drops the in-flight mark. The request itself is not
   * cancelled — nothing here can cancel it — but its `then` finds a controller
   * whose map, sensor and located store all belong to a different task now,
   * and the guard must not still be closed when the next task's first click
   * arrives.
   */
  override reset(): void {
    this.loadingMmsi = null;
  }

  /**
   * The by-MMSI lookup (1 credit): latest fix + the fields nothing else carries
   * (destination, from port, dimensions). Refreshes the vessel's history record
   * in place server-side, mirrors it locally, and — when the vessel is drawn,
   * or `opts.draw` says to — moves its marker to the fresh fix and reopens the
   * popup there.
   *
   * Returns whether a request was actually sent: several clicks are turned back
   * at the door, and a caller that shows an in-flight mark has to be able to
   * tell those apart from a real one. `onSettled` then fires exactly once when
   * the round trip ends, on both the success and the failure path.
   */
  refreshVesselLookup(
    mmsi: string,
    opts: { draw?: boolean; select?: boolean; onSettled?: () => void } = {},
  ): boolean {
    const sensor = this.ctx.sensor;
    const taskid = this.ctx.host.collection?.taskId;
    const sensorId = sensor?.sensor_uuid ?? sensor?.id;
    if (!taskid || !sensorId || this.loadingMmsi) return false;
    const refusal = this.ctx.spend ? this.ctx.spend.refuse('lookup', this.price()) : null;
    if (refusal) {
      this.ctx.host.popupService.openPopup(CollectionPopupComponent, refusal, '', '40%');
      return false;
    }

    this.loadingMmsi = mmsi;
    this.ctx.popup.setVesselPopupUpdating(mmsi, true);
    this.ctx.host.apiService
      .lookupVoyageRadarVessel({ taskid, sensorId, mmsi })
      .toPromise()
      .then((res: any) => {
        this.loadingMmsi = null;
        this.ctx.popup.setVesselPopupUpdating(mmsi, false);
        // Before the map work below, not after: the mark's job is to say the
        // ROUND TRIP is over, and placing the vessel is what happens next.
        opts.onSettled?.();
        // The stored counter IS the balance — mirror it so every price line agrees.
        if (res?.apiCreditsRemaining != null && sensor) {
          sensor.apiCreditsRemaining = res.apiCreditsRemaining;
          this.opts.syncCreditLines();
        }
        if (!res?.vessel) {
          this.ctx.host.popupService.openPopup(
            CollectionPopupComponent,
            'The operator has no data for this vessel right now. You were not charged.',
            '',
            '40%'
          );
          return;
        }
        // Mirror the refreshed record into the local sensor doc (the server
        // updated it in place), so the panel rows agree without a refetch.
        const row = vrMirrorVesselLookup(sensor, mmsi, res.vessel);

        // Move the drawn marker to the fresh fix — or draw it, when the lookup
        // came from a hull's popup ("get latest" implies "show me").
        const normalized = vrToLocatedRecord(row);
        if (normalized && (this.ctx.located.isLocatedOnMap(mmsi) || opts.draw)) {
          normalized.kept = true;
          if (normalized.lat != null && normalized.lng != null) {
            this.ctx.located.upsertMarker(normalized);
            if (opts.draw) {
              this.ctx.host.map?.flyTo(
                [normalized.lat, normalized.lng],
                Math.max(this.ctx.host.map?.getZoom?.() ?? 8, 8),
              );
            }
            // A hull fetched from the nearby list ends exactly where a search
            // result does — selected, so the arrival is marked by the halo and
            // the Trace button comes up on it. Scoped to that caller: pressing
            // Update on a card already open is not a "show me this one".
            if (opts.select) this.opts.selectLocated(mmsi, { openPopup: true });
            else this.ctx.located.openInfo(mmsi);
          }
        }
        this.ctx.located.pushLocatedState();
      })
      .catch((err: any) => {
        this.loadingMmsi = null;
        // The card is NOT replaced on this path, so it would spin forever.
        this.ctx.popup.setVesselPopupUpdating(mmsi, false);
        opts.onSettled?.();
        if (err?.error?.apiCreditsRemaining != null && sensor) {
          sensor.apiCreditsRemaining = err.error.apiCreditsRemaining;
        }
        // Failure copy always states the charge outcome — silence here reads
        // as "it took my credit".
        this.ctx.host.popupService.openPopup(
          CollectionPopupComponent,
          voyageSpendErrorMessage(err, 'lookup', this.price()),
          '',
          '40%'
        );
      });
    return true;
  }

  /**
   * The popup's free "Save vessel" on a snapshot hull: persist the
   * identity + fix the AOI refresh ALREADY PAID FOR — no lookup, no charge.
   * Data comes from the drawn live marker, the live features, or (during
   * playback) the newest loaded frame that carries the hull.
   */
  saveSnapshotVessel(mmsi: string): void {
    const m = String(mmsi ?? '').trim();
    if (!m || this.ctx.located.isSaved(m)) return;
    // A located (amber) hull can also be unsaved — its keep may have failed,
    // or it was deleted from the panel while its marker stayed up. It carries
    // its own record, and it is usually NOT in the snapshot, so the lookup
    // below would find nothing and the button would silently do nothing.
    const located = this.ctx.located.recordFor(m);
    if (located) {
      this.ctx.located.keepVessel(located);
      return;
    }
    const props = this.snapshotPropsFor(m);
    if (!props) return;
    const num = (v: any): number | null => {
      // Not defensive noise: `toVoyageVesselFeature` writes `Lat: ''` for a hull
      // with no position, and `Number('')` is 0 — so without this guard a
      // fix-less vessel is saved at 0, 0 and drawn in the Gulf of Guinea.
      if (v == null || (typeof v === 'string' && !v.trim())) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const record: LocatedVesselRecord = {
      mmsi: m,
      name: (props['Name'] ?? props.name ?? '') || null,
      shipType: (props['Ship Type'] ?? props.shipType ?? '') || null,
      imoNumber: props['IMO'] ?? props.imo ?? null,
      callSign: props['Call Sign'] ?? props.callSign ?? null,
      flag: props['Flag'] ?? props.flag ?? null,
      country: props['Country'] ?? props.country ?? null,
      lat: num(props['Lat'] ?? props.lat ?? props.latitude),
      lng: num(props['Lng'] ?? props.lng ?? props.longitude),
      timeUtc: props['Timestamp UTC'] ?? props.time_utc ?? null,
      navStatus: props['Nav Status'] ?? props.navStatus ?? null,
      waterBody: props['Water Body'] ?? props.waterBody ?? null,
      source: 'snapshot',
      query: null,
      kept: false,
      sog: num(props['Speed'] ?? props.sog),
      cog: num(props['Course'] ?? props.cog),
      heading: num(props['Heading'] ?? props.heading),
      destination: null,
      fromPort: null,
      length: num(props['Length'] ?? props.length),
      beam: num(props['Beam'] ?? props.beam),
      maxDraught: null,
    };
    this.ctx.located.keepVessel(record);
  }

  /** A hull's loaded properties: live marker → live features → newest frame. */
  private snapshotPropsFor(mmsi: string): any | null {
    const marker = this.ctx.selection.liveMarkerFor(mmsi);
    if (marker?.feature?.properties) {
      const ll = marker.getLatLng?.();
      return {
        ...marker.feature.properties,
        latitude: ll?.lat ?? marker.feature.properties?.latitude,
        longitude: ll?.lng ?? marker.feature.properties?.longitude,
      };
    }
    const withCoords = (f: any): any => {
      const p = f?.properties ?? f;
      const coords = f?.geometry?.coordinates;
      return coords
        ? { ...p, longitude: p?.longitude ?? coords[0], latitude: p?.latitude ?? coords[1] }
        : p;
    };
    for (const f of this.ctx.snapshot.liveFeatures) {
      const p = f?.properties ?? f;
      if (String(p?.['MMSI'] ?? p?.mmsi ?? '') === mmsi) return withCoords(f);
    }
    const steps = this.ctx.snapshot.loadedSteps;
    for (let i = steps.length - 1; i >= 0; i--) {
      for (const f of steps[i].features ?? []) {
        const p = f?.properties ?? f;
        if (String(p?.['MMSI'] ?? p?.mmsi ?? '') === mmsi) {
          const merged = withCoords(f);
          return {
            ...merged,
            time_utc:
              merged?.['Timestamp UTC'] ?? merged?.time_utc ?? steps[i].time ?? null,
          };
        }
      }
    }
    return null;
  }
}
